import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { getBookingDurationUnit, getPriceUnit } from '../../util/bookingDuration';
import { isFixdeskListing } from '../../util/fixdesk';
import { isVerifiedProvider } from '../../util/verifiedProvider';
import { isBookingProcessAlias, isAboProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import ListingCardImageSlider from './ListingCardImageSlider';
// Direkt statt ueber components/index: ListingCard haengt ohnehin schon im
// Zyklus mit dem Index, und ein weiterer Name darin macht die Auswertungs-
// reihenfolge nur noch fragiler.
import VerifiedBadge from '../VerifiedBadge/VerifiedBadge';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

// Small "recurring" glyph shown next to the price on Abo (subscription) cards.
// fill="none" is set in CSS (.aboIcon) to override the app-wide
// `svg { fill: currentColor }` rule that would otherwise fill the stroke icon.
const SubscriptionIcon = ({ className }) => (
  <svg
    className={className}
    width="13"
    height="13"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="m17 2 4 4-4 4" />
    <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
    <path d="m7 22-4-4 4-4" />
    <path d="M21 13v1a4 4 0 0 1-4 4H3" />
  </svg>
);

// Small clock glyph for the booking-duration chip. fill:none (via .aboIcon /
// .durationIcon in CSS) keeps the app-wide `svg { fill: currentColor }` from
// filling the stroke icon.
const ClockIcon = ({ className }) => (
  <svg
    className={className}
    width="13"
    height="13"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

/**
 * Booking-duration chip: "how long do you book for" for a listing, shown for
 * every offer that has a time-based booking (hour/day/week/month, and fixed via
 * bookingLengthInMinutes). Renders nothing for item/negotiation offers.
 */
const BookingDurationMaybe = ({ publicData, className, iconClassName }) => {
  // TEMPORARY fixdesk fallback (see util/fixdesk.js): the purchase price buys one
  // month, so the chip reads "Monatlich buchbar" like every other monthly listing.
  const unit =
    getBookingDurationUnit(publicData) || (isFixdeskListing(publicData) ? 'month' : null);
  if (!unit) {
    return null;
  }
  return (
    <span className={className}>
      <ClockIcon className={iconClassName} />
      <FormattedMessage id="BookingDuration.label" values={{ unit }} />
    </span>
  );
};

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const isAbo = isAboProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isAbo ? (
    // Subscription (Abo): just the interval unit ("pro Monat") — the "⟳ Abo"
    // image badge already marks the listing as a subscription.
    <span className={css.perUnit}>
      <FormattedMessage
        id="ListingCard.perIntervalAbo"
        values={{ interval: publicData?.billingInterval || 'month' }}
      />
    </span>
  ) : isBookable ? (
    <span className={css.perUnit}>
      {/* getPriceUnit, NOT getBookingDurationUnit: the latter rounds loosely (2880 minutes → "day"),
          which is fine for a duration chip but states a false rate on a price. It labels only when
          the shown amount buys exactly one unit; multi-period packages stay bare, as they were
          before. `fixed` listings whose variant is exactly 43200 min do gain "pro Monat" — but only
          once ListingCard.perUnit in the Console grows week/month branches, since hosted microcopy
          overrides the repo string and today has day/night/hour only. */}
      <FormattedMessage
        id="ListingCard.perUnit"
        values={{ unitType: getPriceUnit(publicData, price?.amount) || publicData?.unitType }}
      />
    </span>
  ) : isFixdeskListing(publicData) ? (
    // TEMPORARY (see util/fixdesk.js): the fixdesk purchase price buys one month,
    // so label it — a bare "CHF 450" reads like a one-off product price.
    <span className={css.perUnit}>
      <FormattedMessage id="Fixdesk.perMonth" />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {boolean} props.showImageSlider whether this surface opts into the photo slider
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
    highImagePriority,
    showImageSlider,
    isAbo,
  } = props;

  const listingImages = currentListing.images || [];
  const firstImage = listingImages.length > 0 ? listingImages[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // The slider is opt-in per surface, never inferred from the image count: several
  // queries (e.g. ProfilePage) don't cap 'limit.images' at all, so counting photos
  // would switch the slider on wherever the API happened to return more than one.
  // It also must stay off inside the map info card and the mobile map rail, which
  // are themselves horizontal scrollers and would swallow the swipe.
  const hasSlider = showImageSlider && listingImages.length > 1;

  const imageEl = hasSlider ? (
    <ListingCardImageSlider
      images={listingImages}
      title={title}
      variantPrefix={variantPrefix}
      renderSizes={renderSizes}
      highImagePriority={highImagePriority}
    />
  ) : highImagePriority ? (
    <ResponsiveImage
      rootClassName={css.rootForImage}
      alt={title}
      image={firstImage}
      variants={variants}
      sizes={renderSizes}
      fetchpriority="high"
      loading="eager"
    />
  ) : (
    <LazyImage
      rootClassName={css.rootForImage}
      alt={title}
      image={firstImage}
      variants={variants}
      sizes={renderSizes}
      fetchpriority="low"
      loading="lazy"
    />
  );

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      {imageEl}
      {isAbo ? (
        // Subscription (Abo) marker, top-right on the card image (red badge) —
        // mirrors the listing detail hero.
        <div className={css.aboCardBadge}>
          <SubscriptionIcon className={css.aboIcon} />
          <FormattedMessage id="ListingCard.aboBadge" />
        </div>
      ) : null}
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {boolean?} props.showImageSlider opt into the swipeable photo slider (search results only)
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    highImagePriority = false,
    showImageSlider = false,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const isAbo = isAboProcessAlias(publicData?.transactionProcessAlias);

  // Das Badge haengt am Titel, weil der immer da ist; nur wenn die Titelzeile
  // fehlt (Eintragstypen ohne Bild rendern den Titel in der Platzhalterkachel),
  // rutscht es in die Anbieterzeile. Es soll genau einmal erscheinen.
  const isVerifiedAuthor = isVerifiedProvider(author);
  const showBadgeInTitle = isVerifiedAuthor && showListingImage;
  const showBadgeInAuthorLine = isVerifiedAuthor && !showBadgeInTitle && showAuthorInfo;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // v10.13.0 gives the card link an aria-label, so a screen reader announces the listing and its
  // price instead of just "link". Built from our own values rather than upstream's
  // getListingCardTranslations: our price line carries the Abo and fixdesk wording, and the label
  // has to say the same thing that is on screen.
  const { formattedPrice: ariaPrice } = priceData(price, config.currency, intl);
  // Das aria-label am <a> ERSETZT den Inhalt fuer die Namensberechnung — das
  // aria-label des Badges im Inneren wuerde sonst nie vorgelesen. Der Hinweis
  // muss deshalb hier mit hinein.
  const cardAriaLabelBase =
    displayPrice(foundListingTypeConfig) && ariaPrice
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: ariaPrice }
        )
      : title;
  // An die tatsaechlich gerenderten Zeichen gebunden, nicht an isVerifiedAuthor:
  // faellt sowohl die Titelzeile (Eintragstyp ohne Bild) als auch die
  // Anbieterzeile (showAuthorInfo={false} — so rendern SearchMapCardRail,
  // SectionOtherListingsMaybe, ProfilePage und die Landing-Grids) weg, erscheint
  // gar kein Badge. Das Label haette den Hinweis trotzdem angesagt, also einer
  // Screenreader-Nutzerin ein Vertrauenszeichen versprochen, das auf der Karte
  // nirgends steht.
  const showsBadge = showBadgeInTitle || showBadgeInAuthorLine;
  const cardAriaLabel = showsBadge
    ? `${cardAriaLabelBase}. ${intl.formatMessage({ id: 'VerifiedBadge.label' })}`
    : cardAriaLabelBase;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      <ListingCardImage
        renderSizes={renderSizes}
        title={title}
        currentListing={currentListing}
        config={config}
        setActivePropsMaybe={setActivePropsMaybe}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        variantPrefix={variantPrefix}
        style={cardStyle}
        showListingImage={showListingImage}
        highImagePriority={highImagePriority}
        showImageSlider={showImageSlider}
        isAbo={isAbo}
      />
      <div className={css.info}>
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
              {showBadgeInTitle ? <VerifiedBadge className={css.verifiedBadge} /> : null}
            </div>
          )}
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              {showBadgeInAuthorLine ? <VerifiedBadge className={css.verifiedBadge} /> : null}
            </div>
          ) : null}
          <BookingDurationMaybe
            publicData={publicData}
            className={css.durationChip}
            iconClassName={css.durationIcon}
          />
        </div>
        {/* Price last, after the title — the visitor identifies the space first, then judges the
            price. Leading with a coloured price made every card shout its number. */}
        <PriceMaybe
          price={price}
          publicData={publicData}
          config={config}
          intl={intl}
          listingTypeConfig={foundListingTypeConfig}
        />
      </div>
    </NamedLink>
  );
};

export default ListingCard;
